/**
 * Language: English by default, Ukrainian and Russian available, choice remembered.
 *
 * `useT()` hands back the whole dictionary rather than a `t('some.key')` lookup, so every string is
 * checked by the compiler at the point of use — a typo is a build error, not a missing phrase in
 * production. Strings that carry a number or a name are functions, because word order differs.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { en, type Dict } from './en'
import { ru } from './ru'
import { uk } from './uk'

export type Lang = 'en' | 'uk' | 'ru'

const DICTS: Record<Lang, Dict> = { en, uk, ru }
const STORE_KEY = 'tthub_lang'

interface Ctx {
  lang: Lang
  t: Dict
  setLang: (l: Lang) => void
}

const I18nContext = createContext<Ctx>({ lang: 'en', t: en, setLang: () => {} })

/** Remembered choice, or English. We deliberately do not sniff the browser locale: a shared test
 *  machine would then greet different people differently, and the default is a product decision. */
function initialLang(): Lang {
  const stored = localStorage.getItem(STORE_KEY)
  return stored === 'uk' || stored === 'ru' || stored === 'en' ? stored : 'en'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang)

  useEffect(() => {
    localStorage.setItem(STORE_KEY, lang)
    document.documentElement.lang = lang
  }, [lang])

  return (
    <I18nContext.Provider value={{ lang, t: DICTS[lang], setLang: setLangState }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useT(): Dict {
  return useContext(I18nContext).t
}

export function useLang(): [Lang, (l: Lang) => void] {
  const { lang, setLang } = useContext(I18nContext)
  return [lang, setLang]
}

export function LanguageSwitcher() {
  const [lang, setLang] = useLang()
  const t = useT()
  return (
    <select
      className="lang"
      value={lang}
      aria-label={t.lang.label}
      title={t.lang.label}
      onChange={(e) => setLang(e.target.value as Lang)}
    >
      <option value="en">EN</option>
      <option value="uk">UA</option>
      <option value="ru">RU</option>
    </select>
  )
}

/** For modules outside React (stream objects, the input channel) that still need phrases. */
export function dict(lang: Lang): Dict {
  return DICTS[lang]
}

export function currentDict(): Dict {
  return DICTS[initialLang()]
}
